import { useCallback, useRef, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { isRangeDateDisabled } from '../utils/activityFormUtils';

type RangeValue = [Dayjs | null, Dayjs | null];
type RangeInfo = { range?: 'start' | 'end' } | undefined;

/**
 * 活动时间区间控件的「开始只能周一、结束只能周日」接法。
 *
 * 为什么不能只看 `info.from`：rc-picker 给的 from 是「本次打开后先选中的那个日期」，和用户点的是哪个框无关，
 * 先点结束框时会把结束框当成开始框判；为什么不能只看聚焦框：两端都选好后，控件会用同一个 disabledDate
 * 回校验两个已选日期，此时判的不是当前聚焦框，开始日会被「结束只能周日」判成禁用、整段清空。
 * 所以：渲染按聚焦框判，两端都选好后已选的两个日期放行。
 */
export const useWeekRangePicker = () => {
    const [activeRange, setActiveRange] = useState<'start' | 'end'>('start');
    // 本次打开后已选的两端；用 ref 是因为控件在同一个事件里先改值再回校验，state 来不及更新
    const pickedRef = useRef<RangeValue>([null, null]);

    const onFocus = useCallback((_: unknown, info: RangeInfo) => {
        setActiveRange(info?.range === 'end' ? 'end' : 'start');
    }, []);

    const onOpenChange = useCallback((open: boolean) => {
        if (open) {
            pickedRef.current = [null, null];
        }
    }, []);

    const onCalendarChange = useCallback((dates: RangeValue | null) => {
        pickedRef.current = [dates?.[0] ?? null, dates?.[1] ?? null];
    }, []);

    const disabledDate = useCallback(
        (current: Dayjs, info?: { from?: Dayjs }) => isRangeDateDisabled(current, activeRange, info?.from, pickedRef.current),
        [activeRange],
    );

    return { onFocus, onOpenChange, onCalendarChange, disabledDate };
};
